import type {ExtractionDiagnosticoFields} from './ExtractionDiagnosticoSection';
import {DiagnosedExtractedFieldCard} from './ExtractedFieldCard';
import {
    extractedDisplayValueIsFilled,
    extractedFieldIsFilled,
    formatCpf,
    getStringArrayValue,
    getStringValue,
    renderExtractedCard
} from './DocTypeExtractedViewShared';

type IdentificacaoDocTypeExtractedViewProps = {
    extractedData: unknown;
    diagnostico?: ExtractionDiagnosticoFields | null;
};

const GENITOR_1_LABEL = 'Nome do genitor 1';
const GENITOR_2_LABEL = 'Nome do genitor 2';

const IDENTIFICACAO_FIELDS = [
    {label: 'CPF', aliases: ['cpf', 'cpf_number', 'identity_data.cpf_number'], formatter: formatCpf},
    {label: 'Nome', aliases: ['nome_completo', 'full_name', 'identity_data.full_name']},
    {label: 'Data de Nascimento', aliases: ['data_nascimento', 'birth_date', 'identity_data.birth_date']},
    {label: 'Nacionalidade', aliases: ['nacionalidade', 'nationality']},
    {label: 'Naturalidade', aliases: ['naturalidade', 'birthplace', 'identity_data.birthplace']},
    {label: 'Número da Identidade', aliases: ['numero_documento', 'rg_numero', 'rg', 'rg_number']},
    {label: 'Órgão Emissor', aliases: ['orgao_emissor', 'issuing_authority']},
    {label: 'Data da Emissão', aliases: ['data_emissao', 'issue_date', 'identity_data.issue_date']},
    {label: 'Data Fim Validade', aliases: ['data_validade', 'expiration_date', 'identity_data.expiration_date']}
] as const;

const FATHER_NAME_ALIASES = [
    'father_full_name',
    'nome_pai',
    'nome_do_pai',
    'pai',
    'identity_data.father_full_name',
    'identity_data.nome_pai',
    'identity_data.nome_do_pai'
] as const;

const MOTHER_NAME_ALIASES = [
    'mother_full_name',
    'nome_mae',
    'nome_da_mae',
    'mae',
    'identity_data.mother_full_name',
    'identity_data.nome_mae',
    'identity_data.nome_da_mae'
] as const;

const PARENTAGE_ALIASES = ['filiacao', 'filiation', 'parentage', 'parents'] as const;

export function IdentificacaoDocTypeExtractedView({
    extractedData,
    diagnostico
}: IdentificacaoDocTypeExtractedViewProps) {
    const filiacao = getStringArrayValue(extractedData, PARENTAGE_ALIASES);
    const genitor1Name = filiacao[0] ?? getStringValue(extractedData, FATHER_NAME_ALIASES) ?? null;
    const genitor2Name = filiacao[1] ?? getStringValue(extractedData, MOTHER_NAME_ALIASES) ?? null;
    const fieldCards = [
        ...IDENTIFICACAO_FIELDS.map((field, index) => ({
            filled: extractedFieldIsFilled(field, extractedData),
            index,
            node: renderExtractedCard(field, extractedData, diagnostico)
        })),
        {
            filled: extractedDisplayValueIsFilled(genitor1Name),
            index: IDENTIFICACAO_FIELDS.length,
            node: (
                <DiagnosedExtractedFieldCard
                    key={GENITOR_1_LABEL}
                    label={GENITOR_1_LABEL}
                    displayValue={genitor1Name ?? '—'}
                    copyText={genitor1Name}
                    diagnostico={diagnostico}
                    scoreFieldKey={[...FATHER_NAME_ALIASES, 'filiacao.0', 'filiacao_0', 'parentage.0', 'parents.0']}
                />
            )
        },
        {
            filled: extractedDisplayValueIsFilled(genitor2Name),
            index: IDENTIFICACAO_FIELDS.length + 1,
            node: (
                <DiagnosedExtractedFieldCard
                    key={GENITOR_2_LABEL}
                    label={GENITOR_2_LABEL}
                    displayValue={genitor2Name ?? '—'}
                    copyText={genitor2Name}
                    diagnostico={diagnostico}
                    scoreFieldKey={[...MOTHER_NAME_ALIASES, 'filiacao.1', 'filiacao_1', 'parentage.1', 'parents.1']}
                />
            )
        }
    ].sort((left, right) => Number(right.filled) - Number(left.filled) || left.index - right.index);

    return (
        <div className="space-y-2">
            {fieldCards.map((item) => item.node)}
        </div>
    );
}
